import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import api from "../config/api";
import "./Profile.css";

const Profile = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLogoutSuccess, setShowLogoutSuccess] = useState(false);
  const [profileImage, setProfileImage] = useState("");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({});
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    password: "",
    password_confirmation: ""
  });
  const [saveLoading, setSaveLoading] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const navigate = useNavigate();
  const { updateUser } = useContext(AuthContext);

  // Interpret backend errors into user-friendly messages
  const interpretErrorMessage = (errorMessage) => {
    const errorMap = {
      // Full house address errors
      "The full house address field must be a string": "Please enter a valid residential address",
      "The full house address field is required": "Residential address is required",
      "full_house_address must be a string": "Please enter a valid residential address",
      
      // Name field errors
      "The first name field is required": "First name is required",
      "The surname field is required": "Last name is required",
      "The first name field must be a string": "Please enter a valid first name",
      "The surname field must be a string": "Please enter a valid last name",
      
      // Email errors
      "The email field is required": "Email address is required",
      "The email field must be a valid email address": "Please enter a valid email address",
      "The email has already been taken": "This email address is already registered",
      
      // Phone number errors
      "The telephone numbers field is required": "Phone number is required",
      "The whatsapp number field is required": "WhatsApp number is required",
      "The telephone numbers must be a valid phone number": "Please enter a valid phone number",
      "The whatsapp number must be a valid phone number": "Please enter a valid WhatsApp number",
      
      // Date of birth errors
      "The date of birth field is required": "Date of birth is required",
      "The date of birth field must be a valid date": "Please enter a valid date of birth",
      
      // Gender errors
      "The gender field is required": "Please select your gender",
      
      // Password errors
      "The current password field is required": "Current password is required to change your password",
      "The password field is required": "New password is required",
      "The password field must be at least 6 characters": "Password must be at least 6 characters long",
      "The password confirmation field is required": "Please confirm your new password",
      "The password confirmation does not match": "New password and confirmation do not match",
      "Current password is incorrect": "The current password you entered is incorrect",
      
      // File upload errors
      "The passport photo must be an image": "Please select a valid image file",
      "The passport photo must be a file of type: jpeg, png, jpg, gif": "Please select a JPEG, PNG, or GIF image",
      "The passport photo may not be greater than 2048 kilobytes": "Profile picture must be less than 2MB",
      
      // Generic errors
      "Failed to update profile": "Unable to save changes. Please try again.",
      "Unauthenticated": "Your session has expired. Please login again.",
      "Network Error": "Unable to connect to server. Please check your internet connection."
    };

    // Check for exact matches first
    if (errorMap[errorMessage]) {
      return errorMap[errorMessage];
    }

    // Check for partial matches
    for (const [key, value] of Object.entries(errorMap)) {
      if (errorMessage.toLowerCase().includes(key.toLowerCase())) {
        return value;
      }
    }

    // Check for common patterns
    if (errorMessage.includes("must be a string")) {
      return "Please enter valid text in all required fields";
    }
    if (errorMessage.includes("is required")) {
      return "Please fill in all required fields";
    }
    if (errorMessage.includes("already been taken")) {
      return "This information is already registered with another account";
    }
    if (errorMessage.includes("valid") && errorMessage.includes("email")) {
      return "Please enter a valid email address";
    }
    if (errorMessage.includes("valid") && errorMessage.includes("phone")) {
      return "Please enter a valid phone number";
    }

    // Default fallback
    return "Please check your information and try again";
  };

  // Show toast notification
  const showToast = (message, type = "error") => {
    const userFriendlyMessage = type === "error" ? interpretErrorMessage(message) : message;
    setToast({ show: true, message: userFriendlyMessage, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 5000);
  };

  useEffect(() => {
    const fetchUserProfile = async () => {
      const token = localStorage.getItem("authToken");
      const storedUserData = localStorage.getItem("userData");

      if (!token) {
        navigate("/login");
        return;
      }

      try {
        setLoading(true);
        setError("");

        console.log("🔄 Fetching user profile from /auth/profile...");

        // Fetch fresh user data from API
        const response = await api.get("/auth/profile");

        console.log("✅ Profile API response:", response.data);

        if (response.data.status === "success" && response.data.data?.user) {
          const user = response.data.data.user;
          setUserData(user);
          
          // Initialize form data with user data
          setFormData({
            first_name: user.first_name || '',
            surname: user.surname || '',
            email: user.email || '',
            telephone_numbers: user.telephone_numbers || [''],
            whatsapp_number: user.whatsapp_number || '',
            date_of_birth: user.date_of_birth || '',
            gender: user.gender || '',
            full_house_address: user.full_house_address || '',
            state_code: user.state_code || '',
            sd_code: user.sd_code || '',
            fc_code: user.fc_code || '',
            lcda_code: user.lcda_code || '',
            lg_code: user.lg_code || '',
            ward_code: user.ward_code || '',
            pu_code: user.pu_code || '',
            membership_category: user.membership_category || ''
          });
          
          // Update localStorage with fresh data
          localStorage.setItem("userData", JSON.stringify(user));
          
          // Set profile image - use profile_photo from response
          if (user.profile_photo) {
            setProfileImage(user.profile_photo);
          } else if (user.files?.passport_photo) {
            setProfileImage(user.files.passport_photo);
          } else {
            // Default profile image
            setProfileImage("/default-profile.png");
          }

          setError(""); // Clear any previous errors

        } else {
          throw new Error("Invalid response format from server");
        }
      } catch (error) {
        console.error("❌ Error fetching profile:", error);
        
        // Fallback to localStorage data if API fails
        if (storedUserData) {
          try {
            const user = JSON.parse(storedUserData);
            setUserData(user);
            setFormData({
              first_name: user.first_name || '',
              surname: user.surname || '',
              email: user.email || '',
              telephone_numbers: user.telephone_numbers || [''],
              whatsapp_number: user.whatsapp_number || '',
              date_of_birth: user.date_of_birth || '',
              gender: user.gender || '',
              full_house_address: user.full_house_address || '',
              state_code: user.state_code || '',
              sd_code: user.sd_code || '',
              fc_code: user.fc_code || '',
              lcda_code: user.lcda_code || '',
              lg_code: user.lg_code || '',
              ward_code: user.ward_code || '',
              pu_code: user.pu_code || '',
              membership_category: user.membership_category || ''
            });

            if (user.profile_photo) {
              setProfileImage(user.profile_photo);
            } else if (user.files?.passport_photo) {
              setProfileImage(user.files.passport_photo);
            }

            setError("Using cached data - unable to connect to server");
          } catch (parseError) {
            console.error("Error parsing stored user data:", parseError);
            setError("Failed to load profile data. Please login again.");
          }
        } else {
          setError("Failed to load profile data. Please login again.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [navigate]);

  const handleEditToggle = () => {
    if (isEditing) {
      // If currently editing, save changes
      handleSaveProfile();
    } else {
      // If not editing, enable edit mode
      setIsEditing(true);
      // Reset password fields when entering edit mode
      setPasswordData({
        current_password: "",
        password: "",
        password_confirmation: ""
      });
      setPasswordError("");
      setSelectedPhoto(null);
    }
  };

  const handleSaveProfile = async () => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      navigate("/login");
      return;
    }

    setSaveLoading(true);
    setPasswordError("");

    try {
      // Create FormData object to handle file upload
      const formDataToSend = new FormData();

      // Append all form fields
      Object.keys(formData).forEach(key => {
        if (key === 'telephone_numbers' && Array.isArray(formData[key])) {
          formData[key].forEach((number, index) => {
            formDataToSend.append(`telephone_numbers[${index}]`, number);
          });
        } else {
          formDataToSend.append(key, formData[key]);
        }
      });

      // Append password fields if provided
      if (passwordData.password) {
        if (passwordData.password !== passwordData.password_confirmation) {
          setPasswordError("New password and confirmation do not match");
          setSaveLoading(false);
          return;
        }
        if (passwordData.password.length < 6) {
          setPasswordError("Password must be at least 6 characters long");
          setSaveLoading(false);
          return;
        }
        
        formDataToSend.append('current_password', passwordData.current_password);
        formDataToSend.append('password', passwordData.password);
        formDataToSend.append('password_confirmation', passwordData.password_confirmation);
      }

      // Append profile photo if selected
      if (selectedPhoto) {
        formDataToSend.append('passport_photo', selectedPhoto);
      }

      console.log("🔄 Updating profile via /user/profile with FormData");

      // Update profile using the correct endpoint for updates
      const response = await api.post("/user/profile", formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log("✅ Profile update response:", response.data);

      if (response.data.status === "success") {
        // Refresh user data after successful update
        const profileResponse = await api.get("/auth/profile");
        
        if (profileResponse.data.status === "success" && profileResponse.data.data?.user) {
          const updatedUser = profileResponse.data.data.user;
          setUserData(updatedUser);
          localStorage.setItem("userData", JSON.stringify(updatedUser));
          
          // Update AuthContext with new user data
          if (updateUser) {
            updateUser(updatedUser);
          }
          
          // Update profile image
          if (updatedUser.profile_photo) {
            setProfileImage(updatedUser.profile_photo);
          } else if (updatedUser.files?.passport_photo) {
            setProfileImage(updatedUser.files.passport_photo);
          }
        }
        
        setIsEditing(false);
        
        // Reset fields
        setPasswordData({
          current_password: "",
          password: "",
          password_confirmation: ""
        });
        setSelectedPhoto(null);
        
        setError(""); // Clear any errors
        showToast("Profile updated successfully!", "success");
      } else {
        throw new Error(response.data.message || "Failed to update profile");
      }
    } catch (error) {
      console.error("❌ Error updating profile:", error);
      
      let errorMessage = "Failed to update profile";
      let detailedErrors = [];
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        if (errorData.message) {
          errorMessage = errorData.message;
        }
        
        // Handle validation errors - extract all error messages
        if (errorData.errors) {
          detailedErrors = Object.values(errorData.errors).flat();
          errorMessage = detailedErrors.join(', ') || errorMessage;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Show user-friendly error in toast
      showToast(errorMessage, "error");

      // Check if it's a password-related error and set password error state
      const userFriendlyError = interpretErrorMessage(errorMessage);
      if (userFriendlyError.toLowerCase().includes("password")) {
        setPasswordError(userFriendlyError);
      }
    } finally {
      setSaveLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (passwordError) {
      setPasswordError("");
    }
  };

  const handlePhoneNumberChange = (index, value) => {
    const updatedNumbers = [...(formData.telephone_numbers || [])];
    updatedNumbers[index] = value;
    setFormData(prev => ({
      ...prev,
      telephone_numbers: updatedNumbers
    }));
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showToast('Please select a valid image file (JPEG, PNG, GIF)', 'error');
      return;
    }

    // Validate file size (max 2MB as per server validation)
    const maxSize = 2 * 1024 * 1024; // 2MB in bytes
    if (file.size > maxSize) {
      showToast('Profile picture must be less than 2MB', 'error');
      return;
    }

    setSelectedPhoto(file);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setProfileImage(e.target.result);
      setImageLoaded(true);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setSelectedPhoto(null);
    // Reset to original profile image
    if (userData?.profile_photo) {
      setProfileImage(userData.profile_photo);
    } else if (userData?.files?.passport_photo) {
      setProfileImage(userData.files.passport_photo);
    } else {
      setProfileImage("/default-profile.png");
    }
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageError = () => {
    setProfileImage("/default-profile.png");
    setImageLoaded(true);
  };

  const handleLogout = () => {
    setShowLogoutSuccess(true);
    
    setTimeout(() => {
      // Clear all authentication data
      localStorage.removeItem('authToken');
      localStorage.removeItem('userData');
      localStorage.removeItem('tokenExpires');
      localStorage.removeItem('jwt_token');
      localStorage.removeItem('current_user_id');
      localStorage.removeItem('user_data');
      navigate("/login");
    }, 2000);
  };

  const closeLogoutModal = () => {
    setShowLogoutSuccess(false);
    // Clear all authentication data
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('tokenExpires');
    localStorage.removeItem('jwt_token');
    localStorage.removeItem('current_user_id');
    localStorage.removeItem('user_data');
    navigate("/login");
  };

  // Loading state
  if (loading) {
    return (
      <div className="profile-container">
        <div className="loading-spinner-container">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="profile-container">
        <div className="error-message">
          No user data found. Please <a href="/login">login again</a>.
        </div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            <span className="toast-message">{toast.message}</span>
          </div>
        </div>
      )}

      {/* Logout Success Modal */}
      {showLogoutSuccess && (
        <div className="logout-modal-overlay">
          <div className="logout-modal">
            <div className="logout-icon">👋</div>
            <h3>Logged Out Successfully</h3>
            <p>You have been securely logged out of your account.</p>
            <div className="logout-details">
              <div className="logout-item">
                <span className="logout-label">Session Ended:</span>
                <span className="logout-value">{new Date().toLocaleTimeString()}</span>
              </div>
              <div className="logout-item">
                <span className="logout-label">Member ID:</span>
                <span className="logout-value">{userData.membership_id}</span>
              </div>
            </div>
            <div className="logout-progress">
              <div className="progress-bar">
                <div className="progress-fill"></div>
              </div>
              <span>Redirecting to login page...</span>
            </div>
            <button className="logout-close-btn" onClick={closeLogoutModal}>
              Go to Login Now
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="profile-header">
        <h2>Profile & ID Card</h2>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-banner">
          {error}
        </div>
      )}

      {/* Membership Status */}
      <div className="membership-status card">
        <div className="status-left">
          <div className="profile-image-container">
            <img 
              src={profileImage} 
              alt="profile" 
              className={`profile-img ${imageLoaded ? 'loaded' : 'loading'}`}
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
            {isEditing && (
              <div className="profile-image-overlay">
                <label htmlFor="profile-upload" className="upload-btn">
                  📷 Change
                </label>
                {selectedPhoto && (
                  <button 
                    className="remove-btn"
                    onClick={handleRemoveImage}
                  >
                    🗑️ Remove
                  </button>
                )}
                <input
                  id="profile-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                />
              </div>
            )}
          </div>
          <div>
            <h3 className="profile-name">
              {userData.first_name} {userData.surname} 
              <span className="volunteer-badge">{userData.membership_category}</span>
            </h3>
            <p className="member-id-display">ID: {userData.membership_id}</p>
            <p className="member-status-badge">
              Status: <span className={`status ${userData.status?.toLowerCase() || 'active'}`}>
                {userData.status || 'Active'}
              </span>
            </p>
          </div>
        </div>
        <div className="status-right">
          <p>
            <strong>Email:</strong> {userData.email}
          </p>
          <p>
            <strong>Phone:</strong> {userData.telephone_numbers?.[0] || userData.whatsapp_number || 'N/A'}
          </p>
          <p>
            <strong>WhatsApp:</strong> {userData.whatsapp_number || 'N/A'}
          </p>
        </div>
      </div>

      {/* Personal Details */}
      <div className="card">
        <div className="section-header">
          <h4>Personal Details</h4>
        </div>
        <div className="form-grid">
          <div>
            <label>First Name</label>
            <input 
              type="text" 
              value={formData.first_name || ''} 
              disabled={!isEditing}
              onChange={(e) => handleInputChange('first_name', e.target.value)}
            />
          </div>
          <div>
            <label>Last Name</label>
            <input 
              type="text" 
              value={formData.surname || ''} 
              disabled={!isEditing}
              onChange={(e) => handleInputChange('surname', e.target.value)}
            />
          </div>
          <div>
            <label>Email</label>
            <input
              type="email"
              value={formData.email || ''}
              disabled={!isEditing}
              onChange={(e) => handleInputChange('email', e.target.value)}
            />
          </div>
          <div>
            <label>Phone Number</label>
            <input
              type="text"
              value={formData.telephone_numbers?.[0] || ''}
              disabled={!isEditing}
              onChange={(e) => handlePhoneNumberChange(0, e.target.value)}
              placeholder="Enter your phone number"
            />
          </div>
          <div>
            <label>WhatsApp Number</label>
            <input
              type="text"
              value={formData.whatsapp_number || ''}
              disabled={!isEditing}
              onChange={(e) => handleInputChange('whatsapp_number', e.target.value)}
              placeholder="Enter your WhatsApp number"
            />
          </div>
          <div>
            <label>Date of Birth</label>
            <input
              type="date"
              value={formData.date_of_birth || ''}
              disabled={!isEditing}
              onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
            />
          </div>
          <div>
            <label>Gender</label>
            <select
              value={formData.gender || ''}
              disabled={!isEditing}
              onChange={(e) => handleInputChange('gender', e.target.value)}
            >
              <option value="">Select Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div className="full-width">
            <label>Residential Address</label>
            <textarea
              value={formData.full_house_address || ''}
              disabled={!isEditing}
              onChange={(e) => handleInputChange('full_house_address', e.target.value)}
              rows="3"
              placeholder="Enter your complete residential address including street, city, and state"
            />
            <div className="field-hint">
              Please provide your complete residential address including street name, house number, city, and state
            </div>
          </div>
        </div>
      </div>

      {/* Change Password Section */}
      {isEditing && (
        <div className="card">
          <div className="section-header">
            <h4>Change Password</h4>
            <p style={{color: '#666', fontSize: '14px', margin: '5px 0 0 0', fontStyle: 'italic'}}>
              Leave blank if you don't want to change password
            </p>
          </div>
          {passwordError && (
            <div className="error-banner">
              {passwordError}
            </div>
          )}
          <div className="form-grid">
            <div>
              <label>Current Password</label>
              <input
                type="password"
                value={passwordData.current_password}
                onChange={(e) => handlePasswordChange('current_password', e.target.value)}
                placeholder="Enter current password"
              />
            </div>
            <div>
              <label>New Password</label>
              <input
                type="password"
                value={passwordData.password}
                onChange={(e) => handlePasswordChange('password', e.target.value)}
                placeholder="Enter new password"
              />
            </div>
            <div>
              <label>Confirm New Password</label>
              <input
                type="password"
                value={passwordData.password_confirmation}
                onChange={(e) => handlePasswordChange('password_confirmation', e.target.value)}
                placeholder="Confirm new password"
              />
            </div>
          </div>
        </div>
      )}

      {/* Political Info */}
      <div className="card">
        <h4>Political Information</h4>
        <div className="political-info-grid">
          <div>
            <label>State</label>
            <input 
              type="text" 
              value={userData.geographic_info?.state || ''} 
              disabled 
            />
          </div>
          <div>
            <label>Senatorial District</label>
            <input 
              type="text" 
              value={userData.geographic_info?.sd || ''} 
              disabled 
            />
          </div>
          <div>
            <label>Federal Constituency</label>
            <input 
              type="text" 
              value={userData.geographic_info?.fc || ''} 
              disabled 
            />
          </div>
          <div>
            <label>Local Government</label>
            <input 
              type="text" 
              value={userData.geographic_info?.lg || ''} 
              disabled 
            />
          </div>
          <div>
            <label>Ward</label>
            <input 
              type="text" 
              value={userData.geographic_info?.ward || ''} 
              disabled 
            />
          </div>
          <div>
            <label>Polling Unit</label>
            <input 
              type="text" 
              value={userData.geographic_info?.pu || ''} 
              disabled 
            />
          </div>
        </div>
      </div>

      {/* Membership Card */}
      <div className="card membership-card">
        <div className="id-card">
          <div className="id-left">
            <p className="id-title">MEMBER ID CARD</p>
            <p className="id-subtitle">Political Association</p>
            <h3 className="id-name">{userData.full_name}</h3>
            <p className="id-member-id">{userData.membership_id}</p>
            <div className="id-details">
              <span>{userData.geographic_info?.ward || 'Ward'}</span>
              <span>{userData.membership_category}</span>
            </div>
          </div>
          <div className="id-right">
            <div className="id-card-image-container">
              <img 
                src={profileImage} 
                alt="profile" 
                className="profile-img-small"
                onLoad={handleImageLoad}
                onError={handleImageError}
              />
            </div>
            <p className="member-since">Member Since {new Date().getFullYear()}</p>
            <p className="member-status">{userData.status || 'Active'}</p>
          </div>
        </div>
        
        <div className="card-actions">
          <button 
            className="edit-btn" 
            onClick={handleEditToggle}
            disabled={saveLoading}
          >
            {saveLoading ? (
              <>
                <div className="spinner-small"></div>
                Saving...
              </>
            ) : isEditing ? 'Save Changes' : 'Edit Profile'}
          </button>
          {isEditing && (
            <button 
              className="cancel-btn"
              onClick={() => {
                setIsEditing(false);
                // Reset form data to original user data
                setFormData({
                  first_name: userData.first_name || '',
                  surname: userData.surname || '',
                  email: userData.email || '',
                  telephone_numbers: userData.telephone_numbers || [''],
                  whatsapp_number: userData.whatsapp_number || '',
                  date_of_birth: userData.date_of_birth || '',
                  gender: userData.gender || '',
                  full_house_address: userData.full_house_address || '',
                  state_code: userData.state_code || '',
                  sd_code: userData.sd_code || '',
                  fc_code: userData.fc_code || '',
                  lcda_code: userData.lcda_code || '',
                  lg_code: userData.lg_code || '',
                  ward_code: userData.ward_code || '',
                  pu_code: userData.pu_code || '',
                  membership_category: userData.membership_category || ''
                });
                // Reset password fields
                setPasswordData({
                  current_password: "",
                  password: "",
                  password_confirmation: ""
                });
                setSelectedPhoto(null);
                setPasswordError("");
                // Reset profile image to original
                if (userData.profile_photo) {
                  setProfileImage(userData.profile_photo);
                } else if (userData.files?.passport_photo) {
                  setProfileImage(userData.files.passport_photo);
                }
              }}
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Logout Button */}
      <div className="logout-container">
        <button className="logout-btn" onClick={handleLogout}>
          Logout
        </button>
      </div>
    </div>
  );
};

export default Profile;